import { findDataProvider } from "fileParsing/definitions";
import { groupFiles } from "fileParsing/groupFiles";
import { determineMimeType } from "fileParsing/mimeType";
import {
  Category,
  FileCategory,
  type JSZipCompressedObject,
  type ProviderDefinition,
  type ProviderFile,
  type ProviderParser,
  type UnparsedFile,
  WorkerStatus,
} from "fileParsing/types";
import { compileFn, fillEpochDates } from "fileParsing/wrangler/compile";
import { extractSchemaFn } from "fileParsing/wrangler/extractSchema";
import { transformFn } from "fileParsing/wrangler/transform";
import { AlgoStep, type WranglerItem } from "fileParsing/wrangler/types";
import { applyExistingTemplates } from "fileParsing/writeload";
import type { WorkerMessage } from "@store/fileParsing/types";
import { FileCategories, MaxFileSize, ParseableExtensions, PictureExtensions } from "constants/filenames";
import type JSZip from "jszip";
import { type JSZipObject, loadAsync } from "jszip";
import { deserializeOutputs } from "@/containers/Visuals/Sankey/parserProcessing";
import { removeDuplicates } from "@/utils/array";
import { pathToFileName, pathToFolder } from "./WranglerWorker";

export class WranglerProcessor {
  onProgress: (percent: number, msg?: string) => void;
  provider!: ProviderDefinition;
  providerParser!: ProviderParser;
  files!: ProviderFile[];
  wranglerItems!: WranglerItem[];

  private constructor(progress: (percent: number, msg?: string) => void) {
    this.onProgress = progress;
  }

  static createFromArchive = async (archive: File, progress: (percent: number, msg?: string) => void) => {
    const instance = new WranglerProcessor(progress);
    instance.onProgress(10, "Unzipping");
    const files = await instance.unzip(archive);
    instance.onProgress(30, "Template Exists Already?");
    const { provider, providerParser } = findDataProvider(files);
    instance.onProgress(70, "Collecting Data");
    const { files: parsedFiles, wranglerItems } = instance.collectData(provider, archive, files, providerParser);
    instance.onProgress(90, "Sending Results");

    instance.provider = provider;
    instance.providerParser = providerParser;
    instance.files = parsedFiles;
    instance.wranglerItems = wranglerItems;

    return instance;
  };

  static matchFiles(wranglerItem: WranglerItem, files: ProviderFile[]) {
    return files.filter((file) =>
      wranglerItem.files?.some(({ id, exportId }) => id === file.id && exportId === file.exportId),
    );
  }

  private generateProviderFile(
    i: number,
    provider: ProviderDefinition,
    zipName: string,
    file: UnparsedFile,
  ): ProviderFile {
    return {
      compressedSize: file.compressedSize,
      fileName: file.fileName,
      zipName,
      fileType: this.getFileType(file.fileName, file.mimeType),
      dataCategory: Category.Other,
      folder: file.folder,
      hash: file.hash,
      date: file.date,
      mimeType: file.mimeType,
      isDir: file.isDir,
      path: file.path,
      data: [],
      binaryData: file.binaryData,
      uncompressedSize: file.uncompressedSize,
      groupBy: "",
      providerId: provider.id,
      id: i,
      exportId: -1,
      parserData: {
        algoStep: this.isParseable(this.getExtension(file.fileName)) ? AlgoStep.ExtractData : AlgoStep.Failed,
        structure: undefined,
        byteData: file.data,
      },
    };
  }

  private collectData(
    provider: ProviderDefinition,
    zipFile: File,
    files: UnparsedFile[],
    parser: ProviderParser,
  ): { files: ProviderFile[]; wranglerItems: WranglerItem[] } {
    console.log("1. Parse and Create File Schema");
    // 1. Parse and Create File Schema
    const providerFiles = files
      .filter((file) => !file.isDir && file.uncompressedSize > 0)
      .map((file, i) => this.generateProviderFile(i, provider, zipFile.name, file))
      .map((file, _, files) => {
        return extractSchemaFn(file, files);
      });
    console.log("1.1 files parsed", providerFiles.length, "files");

    // Get only parsable files
    const { parseableFiles, unparseableFiles } = providerFiles.reduce(
      ({ parseableFiles, unparseableFiles }, file) => {
        if (
          ParseableExtensions.some((ext) => file.fileName.endsWith(ext)) &&
          (file.parserData.structure?.data.length ?? 0) > 0
        ) {
          return {
            unparseableFiles,
            parseableFiles: [...parseableFiles, file],
          };
        }

        return {
          parseableFiles,
          unparseableFiles: [...unparseableFiles, file],
        };
      },
      {
        parseableFiles: [] as ProviderFile[],
        unparseableFiles: [] as ProviderFile[],
      },
    );
    this.onProgress(75);

    console.log("1.1 process Export Files based on Instructions");
    const { items, others } = applyExistingTemplates(parseableFiles, parser.instructions);

    console.log("2. merge similar schemas");
    // 2. Group files
    const wranglerItems = groupFiles(others, -1);
    console.log("2.1 files grouped", wranglerItems.length, "items");

    const compiled = wranglerItems.reduce(
      ({ files, items }, item) => {
        const itemFiles = WranglerProcessor.matchFiles(item, providerFiles);
        const deserializedOutputs = deserializeOutputs(removeDuplicates(itemFiles.map((file) => file.providerId)));

        console.log("3. Suggest data");
        // 3. Suggest data
        const tnfItem = transformFn(item, itemFiles);

        console.log("5. Apply templates to raw data");
        // 4. Compile results
        const compiledFiles = itemFiles.map((file) => compileFn(tnfItem, file, deserializedOutputs));

        return {
          files: files.concat(compiledFiles),
          items: items.concat({
            ...tnfItem,
            id: items.length,
            dataProgress: {
              ...tnfItem.dataProgress,
              didCompile: compiledFiles.some((file) => file.data.length > 0),
            },
          }),
        };
      },
      {
        files: items.reduce((result, item) => result.concat(item.files), [] as ProviderFile[]),
        items: items.map((item) => item.item),
      },
    );

    console.log("2.2 compiled", compiled.items.length, "items");
    return {
      files: fillEpochDates(compiled.files).concat(unparseableFiles),
      wranglerItems: compiled.items,
    };
  }

  private getExtension(str: string): string {
    return str.slice(str.lastIndexOf(".")).toLowerCase();
  }

  private isFilesizeAllowed(file: JSZipObject): boolean {
    return this.getUncompressedSize(file) < MaxFileSize;
  }

  private getFileType(fileName: string, mimeType: string): FileCategory {
    const ext = this.getExtension(fileName);
    for (const typing of FileCategories) {
      if (typing.list.includes(ext) || typing.mimeType.includes(mimeType)) return typing.type;
    }
    return FileCategory.Other;
  }

  private getUncompressedSize(file: JSZipObject): number {
    // This private `_data` property on a JSZipObject uses this interface.
    return (file as JSZipObject & { _data: JSZipCompressedObject })?._data?.uncompressedSize || 1;
  }

  private getCompressedSize(file: JSZipObject): number {
    // This private `_data` property on a JSZipObject uses this interface.
    return (file as JSZipObject & { _data: JSZipCompressedObject })._data?.compressedSize || 1;
  }

  private isParseable(fileExtension: string) {
    return ParseableExtensions.includes(fileExtension);
  }

  private unzip(zipFile: File | Uint8Array): Promise<UnparsedFile[]> {
    return new Promise((resolveMain, rejectMain) => {
      loadAsync(zipFile)
        .then((zip: JSZip) => {
          const unzipper: Promise<UnparsedFile[]>[] = [];
          zip.forEach((relativePath: string, file: JSZipObject) => {
            unzipper.push(
              new Promise((resolve) => {
                const fileExtension = this.getExtension(relativePath);
                const res: UnparsedFile = {
                  path: relativePath.substring(relativePath.indexOf("/") === 0 ? 1 : 0),
                  date: file.date,
                  fileName: pathToFileName(relativePath),
                  folder: pathToFolder(relativePath),
                  isDir: file.dir || fileExtension === ".zip",
                  hash: "", // digestMessage(file),
                  compressedSize: this.getCompressedSize(file),
                  uncompressedSize: this.getUncompressedSize(file),
                  data: null,
                  binaryData: null,
                  mimeType: "unknown/unknown",
                };
                if (this.isFilesizeAllowed(file) && this.isParseable(fileExtension)) {
                  file
                    .async("uint8array")
                    .then((text: Uint8Array) => {
                      if (fileExtension === ".zip") {
                        this.unzip(text)
                          .then((d) => {
                            d.forEach((f) => {
                              f.path = relativePath + "/" + f.path;
                              f.folder = pathToFolder(relativePath) + "/" + f.folder;
                            });
                            res.data = null;
                            resolve([res, ...d]);
                          })
                          .catch((e) => {
                            postMessage({
                              type: WorkerStatus.Warn,
                              message: 'Error parsing inner zip:"' + e,
                            } as WorkerMessage);
                            resolve([res]);
                          });
                      } else {
                        res.data = text;
                        resolve([res]);
                      }
                    })
                    .catch((e) => {
                      postMessage({
                        type: WorkerStatus.Warn,
                        message: e,
                      } as WorkerMessage);
                      resolve([res]);
                    });
                } else {
                  file
                    .async("blob")
                    .then(async (blob) => await determineMimeType(blob))
                    .then(({ blob, mime }) => {
                      // set custom MIME-type and create Object URL
                      res.mimeType = mime;

                      // TODO: These images get loaded into memory, which just further moves us towards a out-of-memory bug
                      // instead we could maybe load them unto offline-storage like IndexDB?
                      if (PictureExtensions.includes(fileExtension)) {
                        res.binaryData = URL.createObjectURL(blob.slice(0, blob.size, mime));
                      }
                      resolve([res]);
                    });
                }
              }),
            );
          });
          Promise.all(unzipper).then((values) => {
            // flatten array
            resolveMain(values.reduce((acc, val) => acc.concat(val), []));
          });
          this.onProgress(25);
        })
        .catch((e) => rejectMain(this.betterError(e.toString())));
    });
  }

  private betterError(e: string) {
    if (
      e ===
      "Error: Can't find end of central directory : is this a zip file ? If it is, see https://stuk.github.io/jszip/documentation/howto/read_zip.html"
    )
      return "Error: Please enter a .zip file.";
    return e;
  }
}
